import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import {
  CogIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  UserIcon,
  ChevronDownIcon,
  HomeIcon,
  BookOpenIcon,
  BriefcaseIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';

interface UserDropdownProps {
  className?: string;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  // const unreadCount = useUnreadMessages(); // Temporarily disabled for debugging
  const unreadCount = 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900 transition-colors"
      >
        <CogIcon className="h-5 w-5" />
        <span className="hidden lg:block">
          {userProfile?.firstName ? `${userProfile.firstName}` : 'Account'}
        </span>
        <ChevronDownIcon 
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            {/* User Info Header */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">
                {userProfile?.firstName} {userProfile?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {userProfile?.email}
              </p>
            </div>

            {/* Navigation Items */}
            <Link
              to="/dashboard"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <HomeIcon className="h-4 w-4 mr-3 text-gray-400" />
              Dashboard
            </Link>

            <Link
              to="/courses"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <BookOpenIcon className="h-4 w-4 mr-3 text-gray-400" />
              Courses
            </Link>

            <Link
              to="/jobs"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <BriefcaseIcon className="h-4 w-4 mr-3 text-gray-400" />
              Jobs
            </Link>

            <Link
              to="/messages"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors relative"
            >
              <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-3 text-gray-400" />
              Messages
              {unreadCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>

            <div className="border-t border-gray-100 my-1"></div>

            {/* Menu Items */}
            <Link
              to="/profile"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <UserIcon className="h-4 w-4 mr-3 text-gray-400" />
              Settings
            </Link>

            <Link
              to="/manage-subscription"
              onClick={closeDropdown}
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <CreditCardIcon className="h-4 w-4 mr-3 text-gray-400" />
              My Subscription
            </Link>

            <div className="border-t border-gray-100 my-1"></div>

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-900 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-red-400" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;